import { addDays, addMonths, endOfMonth, format, parseISO, startOfMonth } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

/**
 * Submissions are stamped with the tablet's local time at the gate, so the
 * dashboard always works in the airport's timezone, whatever the admin's
 * browser is set to.
 */
export const DASHBOARD_TIMEZONE = "Australia/Melbourne";

export type DashboardPeriod =
  | "today"
  | "yesterday"
  | "7d"
  | "30d"
  | "thisMonth"
  | "nextMonth";

export const PERIOD_OPTIONS: Array<{ value: DashboardPeriod; label: string }> = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "thisMonth", label: "This Month" },
  { value: "nextMonth", label: "Next Month" },
];

/**
 * How far ahead "Next Month" can look: next month and the two after it. Its
 * drop-down picks which of those months the forecast covers.
 */
export const MAX_MONTHS_AHEAD = 3;

/** Periods that show a forecast line as well as (or instead of) actuals. */
export const hasForecast = (period: DashboardPeriod): boolean =>
  period === "thisMonth" || period === "nextMonth";

/**
 * Periods that need the forecast endpoint: the month views, and Today, whose
 * hour chart fills the rest of the day with the forecast.
 */
export const fetchesForecast = (period: DashboardPeriod): boolean =>
  hasForecast(period) || period === "today";

/** Single calendar days, shown in half-hour slots rather than hours. */
export const isSingleDay = (period: DashboardPeriod): boolean =>
  period === "today" || period === "yesterday";

/** Periods with no actuals at all: everything shown is forecast. */
export const isForecastOnly = (period: DashboardPeriod): boolean => period === "nextMonth";

/** Today's calendar date in the airport timezone, for naive date maths. */
const airportToday = (now: Date): Date =>
  parseISO(formatInTimeZone(now, DASHBOARD_TIMEZONE, "yyyy-MM-dd"));

/** The months "Next Month" can forecast, nearest first: "November 2026" etc. */
export const forecastMonthOptions = (
  now: Date = new Date()
): Array<{ monthsAhead: number; label: string; shortLabel: string }> => {
  const today = airportToday(now);
  return Array.from({ length: MAX_MONTHS_AHEAD }, (_, index) => {
    const month = addMonths(startOfMonth(today), index + 1);
    return {
      monthsAhead: index + 1,
      label: format(month, "MMMM yyyy"),
      shortLabel: format(month, "MMMM"),
    };
  });
};

/**
 * What a period is called in headings. "Next Month" is named after its month
 * once it looks further ahead than the month that is actually next.
 */
export const periodLabel = (
  period: DashboardPeriod,
  monthsAhead: number = 1,
  now: Date = new Date()
): string => {
  if (period === "nextMonth" && monthsAhead > 1) {
    return format(addMonths(startOfMonth(airportToday(now)), monthsAhead), "MMMM yyyy");
  }
  return PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? "Today";
};

/** Inclusive YYYY-MM-DD bounds for a period, ending today (airport-local). */
export const periodRange = (
  period: DashboardPeriod,
  now: Date = new Date()
): { fromDate: string; toDate: string } => {
  const today = airportToday(now);
  const iso = (d: Date) => format(d, "yyyy-MM-dd");

  switch (period) {
    case "yesterday": {
      const y = addDays(today, -1);
      return { fromDate: iso(y), toDate: iso(y) };
    }
    case "7d":
      return { fromDate: iso(addDays(today, -6)), toDate: iso(today) };
    case "30d":
      return { fromDate: iso(addDays(today, -29)), toDate: iso(today) };
    case "thisMonth":
      return { fromDate: iso(startOfMonth(today)), toDate: iso(today) };
    case "nextMonth":
    case "today":
    default:
      return { fromDate: iso(today), toDate: iso(today) };
  }
};

/**
 * Inclusive YYYY-MM-DD bounds a forecast period covers: today itself, the
 * whole of this month (so the forecast can be compared with the actuals so
 * far) or the whole of a coming month, `monthsAhead` from now.
 */
export const forecastRange = (
  period: DashboardPeriod,
  monthsAhead: number = 1,
  now: Date = new Date()
): { fromDate: string; toDate: string } => {
  const today = airportToday(now);
  const iso = (d: Date) => format(d, "yyyy-MM-dd");
  if (period === "today") {
    return { fromDate: iso(today), toDate: iso(today) };
  }
  const month = period === "nextMonth" ? addMonths(startOfMonth(today), monthsAhead) : today;
  return { fromDate: iso(startOfMonth(month)), toDate: iso(endOfMonth(month)) };
};

/** The half-hour slot (0..47) the airport clock is in right now. */
export const currentSlot = (now: Date = new Date()): number => {
  const [hour, minute] = formatInTimeZone(now, DASHBOARD_TIMEZONE, "HH:mm").split(":");
  return parseInt(hour, 10) * 2 + Math.floor(parseInt(minute, 10) / 30);
};

/** "15 Mar" style axis label for a YYYY-MM-DD string. */
export const shortDay = (isoDate: string): string => format(parseISO(isoDate), "d MMM");

/** "EEE d MMM" style tooltip label for a YYYY-MM-DD string. */
export const longDay = (isoDate: string): string => format(parseISO(isoDate), "EEE d MMM yyyy");

export const hourLabel = (hour: number, minute: number = 0): string =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
